/**
 * The `claude` CLI process boundary: resolve the binary, build its argv, and
 * interpret its exit when it dies before producing a result. Kept pure so it
 * can be unit-tested without a real spawn.
 */

export interface SpawnArgsInput {
  resume?: string;
  sessionId?: string;
  model: string;
  permissionMode: "plan" | "bypassPermissions";
  settingsPath: string;
  /** Extra system-prompt text appended via `--append-system-prompt`. */
  appendSystemPrompt?: string;
  mcpConfigPath?: string;
  /** When set with mcpConfigPath, the CLI uses ONLY that config and ignores the
   * user's `~/.claude.json` / project `.mcp.json` servers. */
  strictMcpConfig?: boolean;
}

export function resolveClaudeBinary(): string {
  return process.env.CONVEYOR_CLAUDE_BIN ?? "claude";
}

export function buildSpawnArgs(input: SpawnArgsInput): string[] {
  const args: string[] = [];
  if (input.resume) {
    args.push("--resume", input.resume);
  } else if (input.sessionId) {
    args.push("--session-id", input.sessionId);
  }
  args.push("--model", input.model);
  if (input.permissionMode === "bypassPermissions") {
    args.push("--dangerously-skip-permissions");
  } else {
    args.push("--permission-mode", "plan");
  }
  args.push("--settings", input.settingsPath);
  if (input.appendSystemPrompt) {
    args.push("--append-system-prompt", input.appendSystemPrompt);
  }
  if (input.mcpConfigPath) {
    args.push("--mcp-config", input.mcpConfigPath);
    if (input.strictMcpConfig) {
      args.push("--strict-mcp-config");
    }
  }
  return args;
}

// ─── Exit diagnostics ──────────────────────────────────────────────────────
// The PTY harness never turns raw `claude` stdout/stderr into HarnessEvents —
// it relays them to the S5 terminal instead. So when `claude` dies before
// emitting a transcript result, the captured agent logs show only the generic
// "claude exited (code N) without a result"; the real reason (a missing binary,
// an auth/onboarding stop, an unknown CLI flag) scrolled past in the live
// terminal only. These helpers fold a bounded, ANSI-stripped tail of that
// scrollback back into the error so the failure is self-describing in the logs.

// ESC (0x1B) built via fromCharCode so no control char appears as a source
// literal (keeps the no-control-regex lint rule happy). Matches CSI sequences
// (ESC [ ... final-byte) — the bulk of TUI escape noise; any stray ESC bytes
// from other sequences are removed by the control-char pass below.
const ANSI_CSI = new RegExp(`${String.fromCharCode(27)}\\[[0-9;?]*[ -/]*[@-~]`, "g");

/**
 * Strip ANSI escapes + terminal control noise and collapse to the last few
 * readable lines, capped at `maxChars`. Returns "" when nothing printable
 * remains (e.g. the process produced no output at all).
 */
export function cleanTerminalOutput(raw: string, maxChars = 1200): string {
  const noAnsi = raw.replace(ANSI_CSI, "");
  // Drop C0 control chars (and DEL) except tab; fold CR into newline.
  let out = "";
  for (const ch of noAnsi) {
    const code = ch.charCodeAt(0);
    if (ch === "\r" || ch === "\n") out += "\n";
    else if (ch === "\t") out += ch;
    else if (code < 0x20 || code === 0x7f) continue;
    else out += ch;
  }
  const lines = out
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0);
  const text = lines.join("\n").trim();
  return text.length > maxChars ? `…${text.slice(-maxChars)}` : text;
}

/**
 * True when the terminal tail shows node-pty failed to exec the target binary
 * (missing `claude` CLI on PATH). node-pty prints `execvp(3) failed.: No such
 * file or directory` to the pty and the child exits with code 1.
 */
export function isMissingBinaryFailure(tail: string): boolean {
  return /execvp\(\d+\) failed|no such file or directory|command not found/i.test(tail);
}

/**
 * Build the `errors[]` for a `claude` process that exited before emitting a
 * transcript result. The first entry is kept byte-for-byte stable so existing
 * consumers/log scrapers that key off it keep working; richer context is
 * appended after it.
 */
export function buildExitErrors(exitCode: number, rawOutput: string, binary: string): string[] {
  const errors = [`claude exited (code ${exitCode}) without a result`];
  const tail = cleanTerminalOutput(rawOutput);
  if (isMissingBinaryFailure(tail)) {
    errors.push(
      `The \`${binary}\` CLI could not be started — it is not installed or not on PATH. ` +
        `Install the Claude Code CLI in this environment (npm i -g @anthropic-ai/claude-code) ` +
        `or set CONVEYOR_CLAUDE_BIN to its absolute path.`,
    );
  }
  if (tail) {
    errors.push(`Last terminal output before exit:\n${tail}`);
  }
  return errors;
}
